import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Popconfirm,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import { api, type Sensor, type LiveSensorSummary } from "../api";
import SensorForm from "../components/SensorForm";
import { useRole } from "../contexts/AuthContext";

// fmtUptime converts SNMP sysUpTime seconds into a compact human string.
function fmtUptime(sec: number | undefined): string {
  if (!sec || sec <= 0) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// fmtRate is the small shared formatter used by the four live-rate
// columns. Returns "—" for zero so the table doesn't show a forest of
// "0" cells when several sensors are silent.
function fmtRate(v: number, kind: "bps" | "pps"): string {
  if (!v || v <= 0) return "—";
  if (kind === "bps") {
    if (v >= 1e9) return (v / 1e9).toFixed(2) + " G";
    if (v >= 1e6) return (v / 1e6).toFixed(1) + " M";
    if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
    return String(Math.round(v));
  }
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}

export default function SFlowSources() {
  const { isAdmin, isOperator } = useRole();
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingID, setEditingID] = useState<number | undefined>(undefined);
  // Per-sensor current rolling-window rates from /api/v1/live/sensors-
  // summary. Refreshes every 5 s independently of the static sensor
  // config so a polling failure doesn't blank the rest of the table.
  // Keyed by sensor_id for O(1) join in the column renderer.
  const [rates, setRates] = useState<Map<number, LiveSensorSummary>>(new Map());

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.sensors();
      setSensors(s);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Live-rate poller, runs every 5 s while the page is mounted. Errors
  // are swallowed to keep the static sensor list visible — the rate
  // columns just keep their last good values.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const rows = await api.liveSensorsSummary();
        if (cancelled) return;
        const m = new Map<number, LiveSensorSummary>();
        for (const r of rows) m.set(r.sensor_id, r);
        setRates(m);
      } catch {
        /* keep stale */
      }
    };
    tick();
    const id = window.setInterval(tick, 5000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  async function onDelete(id: number) {
    try {
      await api.deleteSensor(id);
      message.success("Sensor deleted");
      reload();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  }

  const columns: ColumnsType<Sensor> = [
    { title: "ID", dataIndex: "id", width: 60 },
    {
      title: "Name",
      dataIndex: "name",
      render: (v: string, row) => <Link to={`/sensors/${row.id}`}>{v}</Link>,
    },
    {
      title: "Protocol",
      dataIndex: "protocol",
      width: 100,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    { title: "Listen port", dataIndex: "listen_port", width: 110 },
    {
      title: "Zones",
      width: 120,
      render: (_: unknown, row: Sensor) => {
        const ids =
          row.zone_ids && row.zone_ids.length > 0
            ? row.zone_ids
            : row.zone_id
              ? [row.zone_id]
              : [];
        if (ids.length === 0) return <span style={{ color: "var(--muted)" }}>—</span>;
        return (
          <Space size={4} wrap>
            {ids.map((z, i) => (
              <Tag key={z} color={i === 0 ? "blue" : undefined}>
                {z}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      width: 90,
      render: (v: boolean) =>
        v ? <Badge status="success" text="on" /> : <Badge status="default" text="off" />,
    },
    {
      title: "Uptime",
      dataIndex: "uptime_sec",
      width: 90,
      align: "right" as const,
      render: (v: number | undefined) => (
        <span style={{ color: v ? undefined : "var(--muted)", fontVariantNumeric: "tabular-nums" }}>
          {fmtUptime(v)}
        </span>
      ),
    },
    // Four live-rate columns (Wansight Sensors-overview parity).
    // Numbers come from /api/v1/live/sensors-summary which polls TSDB
    // for the same window the chart shows.
    //
    // Live status semantics: trust the backend's `online` flag because
    // it knows the per-sensor protocol + active-timeout (sFlow uses 30 s,
    // NetFlow uses active_timeout + 30 s grace). For the not-online
    // case we degrade through "lag" (1× the protocol cadence past the
    // online threshold) → "dead" (silent for ≥ 5 min OR ≥ 3× the
    // sensor's active timeout, whichever is larger). Keying lag/dead
    // off the sensor's configured timeout means a NetFlow sensor with
    // a 5-min active timeout (rare, but allowed) doesn't get flagged
    // dead at minute 5 while it's still healthy.
    {
      title: "Live",
      key: "live",
      width: 70,
      align: "center",
      render: (_: unknown, row: Sensor) => {
        const r = rates.get(row.id);
        if (!r) return <Badge status="default" text="—" />;
        if (r.online) return <Badge status="success" text="ok" />;
        const age = Math.max(0, Math.floor(Date.now() / 1000) - r.as_of);
        const activeTO = row.flows_timeout_active ?? 60;
        const deadAfter = Math.max(300, activeTO * 3);
        if (age <= deadAfter) return <Badge status="warning" text="lag" />;
        return <Badge status="error" text="dead" />;
      },
    },
    {
      title: "BPS IN",
      key: "bps_in",
      width: 100,
      align: "right",
      sorter: (a, b) => (rates.get(a.id)?.bps_in ?? 0) - (rates.get(b.id)?.bps_in ?? 0),
      render: (_: unknown, row: Sensor) =>
        fmtRate(rates.get(row.id)?.bps_in ?? 0, "bps"),
    },
    {
      title: "BPS OUT",
      key: "bps_out",
      width: 100,
      align: "right",
      sorter: (a, b) => (rates.get(a.id)?.bps_out ?? 0) - (rates.get(b.id)?.bps_out ?? 0),
      render: (_: unknown, row: Sensor) =>
        fmtRate(rates.get(row.id)?.bps_out ?? 0, "bps"),
    },
    {
      title: "PPS IN",
      key: "pps_in",
      width: 90,
      align: "right",
      sorter: (a, b) => (rates.get(a.id)?.pps_in ?? 0) - (rates.get(b.id)?.pps_in ?? 0),
      render: (_: unknown, row: Sensor) =>
        fmtRate(rates.get(row.id)?.pps_in ?? 0, "pps"),
    },
    {
      title: "PPS OUT",
      key: "pps_out",
      width: 90,
      align: "right",
      sorter: (a, b) => (rates.get(a.id)?.pps_out ?? 0) - (rates.get(b.id)?.pps_out ?? 0),
      render: (_: unknown, row: Sensor) =>
        fmtRate(rates.get(row.id)?.pps_out ?? 0, "pps"),
    },
    ...(isOperator ? [{
      title: "",
      width: 100,
      render: (_: unknown, row: Sensor) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => { setEditingID(row.id); setFormOpen(true); }}
          />
          {isAdmin && (
            <Popconfirm title={`Delete sensor "${row.name}"?`} onConfirm={() => onDelete(row.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <Card
      title="sFlow Sources"
      extra={
        isOperator && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { setEditingID(undefined); setFormOpen(true); }}
          >
            Add sFlow source
          </Button>
        )
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={sensors}
        columns={columns}
        pagination={false}
        size="middle"
        locale={{ emptyText: "No sensors yet. Click 'Add sFlow source' to configure your first exporter." }}
      />
      <SensorForm
        open={formOpen}
        sensorId={editingID}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
      />
    </Card>
  );
}
